import { ContentObjectInformation, PublisherAPI, PublisherSlotAPI, SystemEvent, SystemSlotEvent } from '@mbrtargeting/metatag-shared-types/metatag-core';
import { addCssToElement, BrowserDetailsInterface, getBoundingClientRect, getBrowserDetails } from '@mbrtargeting/metatag-utils';
import { Config } from '../interfaces/interfaces.js';
import { Registry } from '../loader/di/registry.js';
import { selectCommonSettings, selectFeatureSettings, selectFullConfig } from '../loader/essentials/config-selector.js';
import { triggerEvent } from '../loader/essentials/events.js';
import { MetaTag } from '../loader/essentials/init-metatag.js';
import { logger } from '../loader/essentials/logger.js';
import { ADSLOT_REGISTRY, CONFIG_RESOLVER } from '../loader/token.js';
import { deprecation } from '../utils/deprecation-beacon.js';
import { Adslot } from './modules/adslot-controller/adslot.js';
import { ADHESION_UNIT_WATCHER, CONTENT_OBJECT_WATCHER, GLOBAL_SETTINGS, GLOBAL_TARGETING } from './token.js';

const log = logger.logGroup({ prefix: 'Legacy' });

interface LegacyControllerInterface {
    getPlacements: () => Record<string, PublisherSlotAPI>;
    getPlacementByPosition: (name: string) => PublisherSlotAPI | null;
    getSlotByPosition: (name: string) => PublisherSlotAPI | null;
    getPlacementByAdServerContainerId: (id: string) => PublisherSlotAPI | null;
}

interface LegacyConfigInterface {
    getFilteredKeyValues: () => Record<string, string[]>;
    getZone: () => string;
    getPageType: () => string;
    getContentObject: () => ContentObjectInformation;
    getAdhesionUnit: () => HTMLElement | null;
    _config: Config;
    setCommonValue: () => void;
    setFeatureValue: (key: string, value: unknown) => void;
}

interface LegacyUtilitiesInterface {
    addCssToElement: (node: HTMLElement, cssText: string) => HTMLStyleElement;
    addCssToHead: (cssText: string) => HTMLStyleElement;
    checkOS: () => BrowserDetailsInterface;
    getBrowserData: () => BrowserDetailsInterface;
    setSessionStorageData: (key: string, value: string) => void;
    getSessionStorageData: (key: string) => string | null;
    setLocalStorageData: (key: string, value: string) => void;
    getLocalStorageData: (key: string) => string | null;
    getPos: (element: HTMLElement) => DOMRect;
    getObjectDimensions: (element: HTMLElement) => { left: number; top: number; width: number; height: number; };
    changeAdhesionUnit: (newAdhesionUnit: string) => void;
}

interface LegacyFormatMasterInterface {
    customRenderNodeQuery: string | boolean;
}

interface LegacyFormatControllerInterface {
    getFormat: (formatName: string) => LegacyFormatMasterInterface;
}

declare const SDG: MetaTag & {
    Publisher: {
        getConfig: () => LegacyConfigInterface;
    };
    getEventDispatcher: () => {
        trigger: (eventName: string, passedDataObject: any) => void;
    };
    getFormatController: () => LegacyFormatControllerInterface;
    getCN: () => LegacyControllerInterface;
    getUtil: () => LegacyUtilitiesInterface;
    Utilities: LegacyUtilitiesInterface
    getPUB: () => PublisherAPI;
    KERNEL: Config

};

declare var window: Window & {
    getSDG: () => MetaTag
};

/**
 * Patch some old functions onto SDG object to make ad delivery somehow working.
 * This is a (hopefully) a temporary solution.
 * Will be removed, when templates and other invokers are migrated to new interfaces.
 */
export const patchLegacyStuffToSDG = (): void => {
    const adslotRegistry = Registry.lookup(ADSLOT_REGISTRY);
    const globalSettings = Registry.lookup(GLOBAL_SETTINGS);
    const globalTargeting = Registry.lookup(GLOBAL_TARGETING);
    const adhesionUnitWatcher = Registry.lookup(ADHESION_UNIT_WATCHER);
    const contentObjectWatcher = Registry.lookup(CONTENT_OBJECT_WATCHER);
    const configResolver = Registry.lookup(CONFIG_RESOLVER);

    /**
     * used by some very old [DEV] Templates from WebDev
     */
    window.getSDG = deprecation('window.getSDG', () => {
        return SDG;
    });

    /** translate SDG_SLOT_WILL_RELOAD to metaTagSystemSlotWillReload */
    const mapEventName = (eventName: string): SystemEvent | SystemSlotEvent => {
        const mapping1 = SystemEvent as Record<string, string>;
        const mapping2 = SystemSlotEvent as Record<string, string>;
        // TODO check if event really exists
        return (mapping1[eventName] || mapping2[eventName] || eventName) as SystemEvent | SystemSlotEvent;
    };

    SDG.getEventDispatcher = () => {
        log.deprecated('getEventDispatcher()');
        return {
            trigger: (eventName: string, passedObject: any) => {
                log.deprecated('getEventDispatcher().trigger(%s, %o)', [eventName, passedObject]);
                const mappedName = mapEventName(eventName);
                triggerEvent(mappedName, {
                    passedObject,
                    placement: passedObject,
                    slot: passedObject?.slotName,
                    position: passedObject?.slotName,
                    slotName: passedObject?.slotName,
                });
            },
        };
    };

    /**
     * Old FormatController to access formats and read sub properties for use in templates.
     * Still used in most Wallpaper/Fireplace templates for somewhat unclear use-cases.
     * @returns {{getFormat: (formatName: string) => {customRenderNodeQuery: boolean}}}
     */
    SDG.getFormatController = deprecation('SDG.getFormatController', () => {
        log.deprecated('getFormatController()');
        return {
            getFormat: (formatName: string) => {
                log.deprecated('getFormat()');
                return {
                    customRenderNodeQuery: false
                }
            }
        }
    });

    SDG.getPUB = deprecation('SDG.getPUB', () => {
        return SDG.Publisher;
    });

    SDG.getCN = () => {
        log.deprecated('getCN()');
        return {
            getPlacements: (): Record<string, PublisherSlotAPI> => {
                log.deprecated('getPlacements()');
                return adslotRegistry.getAdslotsMap();
            },
            getPlacementByPosition: (name: string): PublisherSlotAPI | null => {
                log.deprecated('getPlacementByPosition(%s)', [name]);
                return adslotRegistry.getAdslot(name);
            },
            getSlotByPosition: (name: string): PublisherSlotAPI | null => {
                log.deprecated('getSlotByPosition(%s)', [name]);
                return adslotRegistry.getAdslot(name);
            },
            getPlacementByAdServerContainerId: (id: string): PublisherSlotAPI | null => {
                log.deprecated('getPlacementByAdServerContainerId(%s)', [id]);
                return (adslotRegistry.getAdslots() as Adslot[]).find(adslot => adslot.adserverContainer.id === id) ?? null;
            },
        };
    };

    const utilities: LegacyUtilitiesInterface = {
        addCssToElement: (node: HTMLElement, cssText: string) => addCssToElement(cssText, node),
        addCssToHead: (cssText: string) => addCssToElement(cssText, document.head),
        checkOS: getBrowserDetails,
        getBrowserData: getBrowserDetails,
        setSessionStorageData: (key: string, value: string) => {
            log.deprecated('setSessionStorageData(%s, %s)', [key, value]);
            return SDG.Advertising!.setSessionStorageData(key, value)
        },
        getSessionStorageData: (key: string): string | null => {
            //use direct way, instead of storageHelper, since old templates do not expect a promised based return. We assume consent is given.
            log.deprecated('getSessionStorageData(%s)', [key]);
            return window.sessionStorage.getItem(key)
        },
        setLocalStorageData: (key: string, value: string) => {
            log.deprecated('setLocalStorageData(%s, %s)', [key, value]);
            return SDG.Advertising!.setLocalStorageData(key, value)
        },
        getLocalStorageData: (key: string): string | null => {
            //use direct way, instead of storageHelper, since old templates do not expect a promised based return. We assume consent is given.
            log.deprecated('setLocalStorageData(%s)', [key]);
            return window.localStorage.getItem(key)
        },
        getPos: getBoundingClientRect,
        getObjectDimensions: (element: HTMLElement): { left: number; top: number; width: number; height: number; } => {
            const { left, top } = getBoundingClientRect(element);
            const { offsetWidth: width, offsetHeight: height } = element;
            return { left, top, width, height };
        },
        changeAdhesionUnit: (newAdhesionUnit: string) => SDG!.Advertising!.changeAdhesionUnit(newAdhesionUnit)
    };

    SDG.Utilities = deprecation('SDG.Utilities', utilities);

    SDG.getUtil = deprecation('SDG.getUtil', () => {
        log.deprecated('getUtil()');
        return utilities;
    });

    SDG.Publisher.getConfig = deprecation('SDG.Publisher.getConfig', () => {
        log.deprecated('getConfig()');
        return {
            getFilteredKeyValues: () => {
                log.deprecated('getFilteredKeyValues()');
                const { kvFilter = [] } = configResolver.get(selectCommonSettings());
                return globalTargeting.filter(([key]) => !kvFilter.includes(key)).toObject();
            },
            getZone: (): string => {
                return globalSettings.getZone();

            },
            getPageType: (): string => {
                return globalSettings.getPageType();
            },
            getContentObject: (): ContentObjectInformation => {
                return contentObjectWatcher.contentObject;
            },
            _config: configResolver.get(selectFullConfig()),
            getContentObjectPromise: (): Promise<ContentObjectInformation> => {
                return contentObjectWatcher.contentObjectPromise;
            },
            getAdhesionUnit: (): HTMLElement | null => {
                return adhesionUnitWatcher.getAdhesionUnit().node;
            },
            //used by DoubleDynamicSitebar/Advanced Fireplace template
            getStickyStop: (): boolean => {
                log.deprecated('.getConfig().getStickyStop(). Use Advertising getStickyStop instead.');
                return configResolver.get(selectFeatureSettings()).stickyStop || false;
            },
            getStickyStopUnit: (): Element | false => {
                log.deprecated('.getConfig().getStickyStop(). Use Advertising getStickyStop instead.');
                const selector = configResolver.get(selectFeatureSettings()).stickyStopUnitQuery || '';
                return (selector !== '') ? document.querySelector(selector) || false : false;
            },
            setCommonValue: () => {
                log.deprecated('.getConfig().setCommonValue() is no longer supported. Please remove this function call.');
            },
            setFeatureValue: (key: string, value: unknown) => {
                log.deprecated('.getConfig().setFeatureValue() is no longer supported. Please remove this function call.');
                configResolver.update(selectFeatureSettings(), featureSettings => ({
                    ...featureSettings,
                    [key]: value,
                }));
            },
        };
    });
    SDG.KERNEL = deprecation('SDG.KERNEL', configResolver.get(selectFullConfig()));
};
